import React, { useState, useEffect, useCallback } from 'react';
import { Play, Clock, ChevronDown, ChevronRight, BookOpen, Users, Plus, Edit2, Trash2, Copy, Check } from 'lucide-react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';
import './tutorial.css'; // Import your CSS styles

const TutorialSystem = () => {
    const permissions = usePermissions('Tutorials');
    const [selectedUserType, setSelectedUserType] = useState('ShopOwner');
    const [tutorialData, setTutorialData] = useState([]);
    const [expandedModules, setExpandedModules] = useState({});
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCRUD, setShowCRUD] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [editingTutorial, setEditingTutorial] = useState(null);
    const [showAddModule, setShowAddModule] = useState(false);
    const [showAddTutorial, setShowAddTutorial] = useState(null);
    const [shareCopied, setShareCopied] = useState(false);



    // Form states
    const [moduleForm, setModuleForm] = useState({
        name: '',
        description: '',
        order: 0,
        userType: ''
    });

    const [tutorialForm, setTutorialForm] = useState({
        name: '',
        module: '',
        videoUrl: '',
        videoDuration: 1,
        for: 'ShopOwner',
        description: '',
        thumbnail: '',
        order: 0
    });

    // Handle image upload
    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await apiWithAuth.post('/upload/single', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const data = response.data;
            if (data?.file_url) {
                setTutorialForm(prev => ({ ...prev, videoUrl: data.file_url }));
            } else {
                alert("Upload failed: " + (data?.message || "Unknown error"));
            }
        } catch (err) {
            console.error("Upload error:", err);
            alert("Video upload failed. Please try again.");
        }
    };

    const handleThumbnailUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await apiWithAuth.post('/upload/single', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const data = response.data;
            if (data?.file_url) {
                setTutorialForm(prev => ({ ...prev, thumbnail: data.file_url }));
            } else {
                alert("Upload failed: " + (data?.message || "Unknown error"));
            }
        } catch (err) {
            console.error("Thumbnail upload error:", err);
            alert("Thumbnail upload failed. Please try again.");
        }
    };

    // Fetch tutorials from API
    const fetchTutorials = useCallback(async (userType) => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiWithAuth.get(`/tutorials/structure/${userType}`);
            const data = response.data;

            if (data.success) {
                setTutorialData(data.data || []);
                // Auto-expand first module
                if (data.data && data.data.length > 0) {
                    setExpandedModules({ [data.data[0].module._id]: true });
                }
            } else {
                throw new Error(data.message || 'Failed to fetch tutorials');
            }
        } catch (err) {
            console.error('Error fetching tutorials:', err);
            setError(err.message || 'Failed to load tutorials');
            setTutorialData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // CRUD Operations
    const createModule = async (moduleData) => {
        try {
            const response = await apiWithAuth.post('/tutorials/module', moduleData);
            const data = response.data;
            if (data.success) {
                await fetchTutorials(selectedUserType);
                setShowAddModule(false);
                setModuleForm({ name: '', description: '', order: 0 });
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.error('Error creating module:', err);
            setError(err.message);
        }
    };

    const updateModule = async (moduleId, moduleData) => {
        try {
            const response = await apiWithAuth.put(`/tutorials/module/${moduleId}`, moduleData);
            const data = response.data;
            if (data.success) {
                await fetchTutorials(selectedUserType);
                setEditingModule(null);
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.error('Error updating module:', err);
            setError(err.message);
        }
    };

    const deleteModule = async (moduleId) => {
        // if (!confirm('Are you sure you want to delete this module? This will also delete all tutorials in this module.')) {
        //     return;
        // }

        try {
            const response = await apiWithAuth.delete(`/tutorials/module/${moduleId}`);
            const data = response.data;
            if (data.success) {
                await fetchTutorials(selectedUserType);
                setSelectedVideo(null);
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.error('Error deleting module:', err);
            setError(err.message);
        }
    };

    const createTutorial = async (tutorialData) => {
        try {
            const response = await apiWithAuth.post('/tutorials/tutorial', tutorialData);
            const data = response.data;
            if (data.success) {
                await fetchTutorials(selectedUserType);
                setShowAddTutorial(null);
                setTutorialForm({
                    name: '',
                    module: '',
                    videoUrl: '',
                    videoDuration: 1,
                    for: 'ShopOwner',
                    description: '',
                    thumbnail: '',
                    order: 0
                });
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.error('Error creating tutorial:', err);
            setError(err.message);
        }
    };

    const updateTutorial = async (tutorialId, tutorialData) => {
        try {
            const response = await apiWithAuth.put(`/tutorials/tutorial/${tutorialId}`, tutorialData);
            const data = response.data;
            if (data.success) {
                await fetchTutorials(selectedUserType);
                setEditingTutorial(null);
                if (selectedVideo && selectedVideo._id === tutorialId) {
                    setSelectedVideo(data.data);
                }
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.error('Error updating tutorial:', err);
            setError(err.message);
        }
    };

    const deleteTutorial = async (tutorialId) => {
        // if (!confirm('Are you sure you want to delete this tutorial?')) {
        //     return;
        // }

        try {
            const response = await apiWithAuth.delete(`/tutorials/tutorial/${tutorialId}`);
            const data = response.data;
            if (data.success) {
                await fetchTutorials(selectedUserType);
                if (selectedVideo && selectedVideo._id === tutorialId) {
                    setSelectedVideo(null);
                }
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.error('Error deleting tutorial:', err);
            setError(err.message);
        }
    };

    useEffect(() => {
        fetchTutorials(selectedUserType);
    }, [selectedUserType, fetchTutorials]);

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const toggleModule = (moduleId) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }));
    };

    const userTypeIcons = {
        ShopOwner: <Users className="w-5 h-5" />,
        ShopMember: <BookOpen className="w-5 h-5" />,
        DeliveryBoy: <Play className="w-5 h-5" />,
        User: <Users className="w-5 h-5" />
    };

    const handleModuleSubmit = (e) => {
        e.preventDefault();
        if (editingModule) {
            updateModule(editingModule._id, moduleForm);
        } else {
            createModule(moduleForm);
        }
    };

    const handleTutorialSubmit = (e) => {
        e.preventDefault();
        if (editingTutorial) {
            updateTutorial(editingTutorial._id, tutorialForm);
        } else {
            createTutorial(tutorialForm);
        }
    };

    const startEditModule = (module) => {
        setEditingModule(module);
        setModuleForm({
            name: module.name,
            description: module.description || '',
            order: module.order || 0
        });
    };

    const startEditTutorial = (tutorial) => {
        setEditingTutorial(tutorial);
        setTutorialForm({
            name: tutorial.name,
            module: tutorial.module._id,
            videoUrl: tutorial.videoUrl,
            videoDuration: tutorial.videoDuration,
            for: tutorial.for,
            description: tutorial.description || '',
            thumbnail: tutorial.thumbnail || '',
            order: tutorial.order || 0
        });
    };

    const startAddTutorial = (moduleId) => {
        setShowAddTutorial(moduleId);
        setTutorialForm({
            name: '',
            module: moduleId,
            videoUrl: '',
            videoDuration: 1,
            for: selectedUserType,
            description: '',
            thumbnail: '',
            order: 0
        });
    };

    // Share video link
    const shareVideoLink = async (videoUrl) => {
        try {
            await navigator.clipboard.writeText(videoUrl);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = videoUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading tutorials...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tutorials-error-container">
                <div className="tutorials-error-card">
                    <h3 className="tutorials-error-title">Error Loading Tutorials</h3>
                    <p className="tutorials-error-message">{error}</p>
                    <button
                        onClick={() => fetchTutorials(selectedUserType)}
                        className="tutorials-error-button"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="tutorials-container">
            {/* Header */}
            <div className="tutorials-header">
                <div className="tutorials-header-container">
                    <div className="tutorials-header-content">
                        <h1 className="tutorials-title">Tutorial System</h1>

                        <div className="tutorials-header-actions">
                            <button
                                onClick={() => setShowCRUD(!showCRUD)}
                                className={`tutorials-mode-button ${showCRUD ? 'admin' : 'view'}`}
                            >
                                {showCRUD ? 'View Mode' : 'Admin Mode'}
                            </button>

                            <div className="tutorials-role-selector">
                                <label className="tutorials-role-label">Role:</label>
                                <select
                                    value={selectedUserType}
                                    onChange={(e) => setSelectedUserType(e.target.value)}
                                    className="tutorials-role-select"
                                >
                                    <option value="ShopOwner">Shop Owner</option>
                                    <option value="ShopMember">Shop Member</option>
                                    <option value="DeliveryBoy">Delivery Boy</option>
                                    <option value="User">User</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tutorials-main">
                <div className="tutorials-grid">
                    {/* Sidebar - Module List */}
                    <div className="tutorials-sidebar">
                        <div className="tutorials-sidebar-header">
                            <div className="tutorials-sidebar-title-container">
                                <div className="tutorials-sidebar-title">
                                    {userTypeIcons[selectedUserType]}
                                    <span>
                                        {selectedUserType.replace(/([A-Z])/g, ' $1').trim()} Tutorials
                                    </span>
                                </div>
                                {showCRUD && (
                                    <button
                                        onClick={() => setShowAddModule(true)}
                                        className="tutorials-add-module-button"
                                        title="Add Module"
                                    >
                                        <Plus className="tutorials-add-icon" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="tutorials-module-list">
                            {tutorialData.length === 0 ? (
                                <div className="tutorials-empty-state">
                                    <BookOpen className="tutorials-empty-icon" />
                                    <p className="tutorials-empty-message">
                                        No tutorials available for {selectedUserType}
                                    </p>
                                    <button
                                        onClick={() => fetchTutorials(selectedUserType)}
                                        className="tutorials-refresh-button"
                                    >
                                        Refresh
                                    </button>
                                </div>
                            ) : (
                                tutorialData.map((moduleData) => (
                                    <div key={moduleData.module._id} className="tutorials-module-item">
                                        <div className="tutorials-module-header">
                                            <button
                                                onClick={() => toggleModule(moduleData.module._id)}
                                                className="tutorials-module-toggle"
                                            >
                                                {expandedModules[moduleData.module._id] ? (
                                                    <ChevronDown className="tutorials-module-chevron" />
                                                ) : (
                                                    <ChevronRight className="tutorials-module-chevron" />
                                                )}
                                                <div className="tutorials-module-info">
                                                    <h3 className="tutorials-module-name">{moduleData.module.name}</h3>
                                                    <p className="tutorials-module-count">
                                                        {moduleData.tutorials.length} video{moduleData.tutorials.length !== 1 ? 's' : ''}
                                                    </p>
                                                    {moduleData.module.description && (
                                                        <p className="tutorials-module-description">{moduleData.module.description}</p>
                                                    )}
                                                </div>
                                            </button>

                                            {showCRUD && (
                                                <div className="tutorials-module-actions">
                                                    {permissions.canUpdate && (
                                                        <button
                                                            onClick={() => startEditModule(moduleData.module)}
                                                            className="tutorials-action-button tutorials-edit-button"
                                                            title="Edit Module"
                                                        >
                                                            <Edit2 className="tutorials-edit-icon" />
                                                        </button>
                                                    )}
                                                    {permissions.canCreate && (
                                                        <button
                                                            onClick={() => startAddTutorial(moduleData.module._id)}
                                                            className="tutorials-action-button tutorials-add-button"
                                                            title="Add Tutorial"
                                                        >
                                                            <Plus className="tutorials-add-icon" />
                                                        </button>
                                                    )}
                                                    {permissions.canDelete && (
                                                        <button
                                                            onClick={() => deleteModule(moduleData.module._id)}
                                                            className="tutorials-action-button tutorials-delete-button"
                                                            title="Delete Module"
                                                        >
                                                            <Trash2 className="tutorials-delete-icon" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {expandedModules[moduleData.module._id] && (
                                            <div className="tutorials-tutorial-list">
                                                {moduleData.tutorials.map((tutorial) => (
                                                    <div key={tutorial._id} className="tutorials-tutorial-item">
                                                        <button
                                                            onClick={() => setSelectedVideo(tutorial)}
                                                            className={`tutorials-tutorial-button ${selectedVideo?._id === tutorial._id ? 'active' : ''
                                                                }`}
                                                        >
                                                            <Play className="tutorials-play-icon" />
                                                            <div>
                                                                <p className="tutorials-tutorial-name">{tutorial.name}</p>
                                                                <div className="tutorials-tutorial-duration">
                                                                    <Clock className="tutorials-clock-icon" />
                                                                    <span>{formatDuration(tutorial.videoDuration)}</span>
                                                                </div>
                                                            </div>
                                                        </button>

                                                        {showCRUD && (
                                                            <div className="tutorials-module-actions">
                                                                {permissions.canUpdate && (
                                                                    <button
                                                                        onClick={() => startEditTutorial(tutorial)}
                                                                        className="tutorials-action-button tutorials-edit-button"
                                                                        title="Edit Tutorial"
                                                                    >
                                                                        <Edit2 className="tutorials-edit-icon" />
                                                                    </button>
                                                                )}
                                                                {permissions.canDelete && (
                                                                    <button
                                                                        onClick={() => deleteTutorial(tutorial._id)}
                                                                        className="tutorials-action-button tutorials-delete-button"
                                                                        title="Delete Tutorial"
                                                                    >
                                                                        <Trash2 className="tutorials-delete-icon" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Main Content - Video Player */}
                    <div className="tutorials-video-container">
                        {selectedVideo ? (
                            <div>
                                <div className="tutorials-video-player">
                                    <video
                                        key={selectedVideo._id}
                                        controls
                                        className="tutorials-video-element"
                                        poster={selectedVideo.thumbnail}
                                    >
                                        <source src={selectedVideo.videoUrl} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>

                                <div className="tutorials-video-details">
                                    <h2 className="tutorials-video-title">{selectedVideo.name}</h2>

                                    <div className="tutorials-video-meta">
                                        <div className="tutorials-video-duration">
                                            <Clock className="tutorials-clock-icon" />
                                            <span>{formatDuration(selectedVideo.videoDuration)}</span>
                                        </div>
                                        {selectedVideo.videoUrl && (
                                            <button
                                                onClick={() => shareVideoLink(selectedVideo.videoUrl)}
                                                className="tutorials-share-button"
                                                title="Share video link"
                                            >
                                                {shareCopied ? (
                                                    <>
                                                        <Check className="tutorials-share-icon" />
                                                        <span>Copied!</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="tutorials-share-icon" />
                                                        <span>Share Link</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {selectedVideo.description && (
                                        <p className="tutorials-video-description">{selectedVideo.description}</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="tutorials-video-placeholder">
                                <div className="tutorials-placeholder-content">
                                    <Play className="tutorials-play-placeholder" />
                                    <h3 className="tutorials-placeholder-title">Select a tutorial to watch</h3>
                                    <p className="tutorials-placeholder-message">
                                        Choose a video from the sidebar to get started
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Module Modal */}
            {showAddModule && (
                <div className="tutorials-modal-overlay">
                    <div className="tutorials-modal">
                        <h3 className="tutorials-modal-title">Add New Module</h3>
                        <form onSubmit={handleModuleSubmit}>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Name *</label>
                                <input
                                    type="text"
                                    value={moduleForm.name}
                                    onChange={(e) => setModuleForm({ ...moduleForm, name: e.target.value })}
                                    className="tutorials-form-input"
                                    required
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">For *</label>
                                <select
                                    value={moduleForm.for}
                                    onChange={(e) => setModuleForm({ ...moduleForm, userType: e.target.value })}
                                    className="tutorials-form-input"
                                    required
                                >
                                    <option value=""> -- Select user type -- </option>
                                    <option value="ShopOwner">ShopOwner</option>
                                    <option value="ShopMember">ShopMember</option>
                                    <option value="DeliveryBoy">DeliveryBoy</option>
                                    <option value="User">User</option>
                                </select>
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Description</label>
                                <textarea
                                    value={moduleForm.description}
                                    onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                                    className="tutorials-form-textarea"
                                    rows="3"
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Order</label>
                                <input
                                    type="number"
                                    value={moduleForm.order}
                                    onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })}
                                    className="tutorials-form-input"
                                />
                            </div>
                            <div className="tutorials-modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModule(false)}
                                    className="tutorials-cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="tutorials-submit-button"
                                >
                                    Create Module
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Module Modal */}
            {editingModule && (
                <div className="tutorials-modal-overlay">
                    <div className="tutorials-modal">
                        <h3 className="tutorials-modal-title">Edit Module</h3>
                        <form onSubmit={handleModuleSubmit}>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Name *</label>
                                <input
                                    type="text"
                                    value={moduleForm.name}
                                    onChange={(e) => setModuleForm({ ...moduleForm, name: e.target.value })}
                                    className="tutorials-form-input"
                                    required
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Description</label>
                                <textarea
                                    value={moduleForm.description}
                                    onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                                    className="tutorials-form-textarea"
                                    rows="3"
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Order</label>
                                <input
                                    type="number"
                                    value={moduleForm.order}
                                    onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })}
                                    className="tutorials-form-input"
                                />
                            </div>
                            <div className="tutorials-modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setEditingModule(null)}
                                    className="tutorials-cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="tutorials-submit-button"
                                >
                                    Update Module
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Tutorial Modal */}
            {showAddTutorial && (
                <div className="tutorials-modal-overlay">
                    <div className="tutorials-modal tutorials-modal-lg">
                        <h3 className="tutorials-modal-title">Add New Tutorial</h3>
                        <form onSubmit={handleTutorialSubmit}>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Name *</label>
                                <input
                                    type="text"
                                    value={tutorialForm.name}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, name: e.target.value })}
                                    className="tutorials-form-input"
                                    required
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Upload Video *</label>

                                <input
                                    type="file"
                                    accept="video/*"
                                    onChange={handleVideoUpload}
                                    className="tutorials-form-input"
                                    required
                                />

                                {tutorialForm.videoUrl && (
                                    <div className="mt-2">
                                        <a
                                            href={tutorialForm.videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline"
                                        >
                                            View Uploaded Video
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Duration (seconds) *</label>
                                <input
                                    type="number"
                                    value={tutorialForm.videoDuration}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, videoDuration: parseInt(e.target.value) })}
                                    className="tutorials-form-input"
                                    required
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">User Type *</label>
                                <select
                                    value={tutorialForm.for}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, for: e.target.value })}
                                    className="tutorials-form-select"
                                    required
                                >
                                    <option value="ShopOwner">Shop Owner</option>
                                    <option value="ShopMember">Shop Member</option>
                                    <option value="DeliveryBoy">Delivery Boy</option>
                                    <option value="User">User</option>
                                </select>
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Description</label>
                                <textarea
                                    value={tutorialForm.description}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, description: e.target.value })}
                                    className="tutorials-form-textarea"
                                    rows="3"
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Upload Thumbnail</label>

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleThumbnailUpload}
                                    className="tutorials-form-input"
                                />

                                {tutorialForm.thumbnail && (
                                    <div className="mt-2">
                                        <a
                                            href={tutorialForm.thumbnail}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline break-all"
                                        >
                                            View Uploaded Thumbnail
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Order</label>
                                <input
                                    type="number"
                                    value={tutorialForm.order}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, order: parseInt(e.target.value) })}
                                    className="tutorials-form-input"
                                />
                            </div>
                            <div className="tutorials-modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setShowAddTutorial(null)}
                                    className="tutorials-cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="tutorials-submit-button"
                                >
                                    Create Tutorial
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Tutorial Modal */}
            {editingTutorial && (
                <div className="tutorials-modal-overlay">
                    <div className="tutorials-modal tutorials-modal-lg">
                        <h3 className="tutorials-modal-title">Edit Tutorial</h3>
                        <form onSubmit={handleTutorialSubmit}>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Name *</label>
                                <input
                                    type="text"
                                    value={tutorialForm.name}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, name: e.target.value })}
                                    className="tutorials-form-input"
                                    required
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Upload Video *</label>

                                <input
                                    type="file"
                                    accept="video/*"
                                    onChange={handleVideoUpload}
                                    className="tutorials-form-input"
                                    required
                                />

                                {tutorialForm.videoUrl && (
                                    <div className="mt-2">
                                        <a
                                            href={tutorialForm.videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline"
                                        >
                                            View Uploaded Video
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Duration (seconds) *</label>
                                <input
                                    type="number"
                                    value={tutorialForm.videoDuration}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, videoDuration: parseInt(e.target.value) })}
                                    className="tutorials-form-input"
                                    required
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">User Type *</label>
                                <select
                                    value={tutorialForm.for}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, for: e.target.value })}
                                    className="tutorials-form-select"
                                    required
                                >
                                    <option value="ShopOwner">Shop Owner</option>
                                    <option value="ShopMember">Shop Member</option>
                                    <option value="DeliveryBoy">Delivery Boy</option>
                                    <option value="User">User</option>
                                </select>
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Description</label>
                                <textarea
                                    value={tutorialForm.description}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, description: e.target.value })}
                                    className="tutorials-form-textarea"
                                    rows="3"
                                />
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Upload Thumbnail</label>

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleThumbnailUpload}
                                    className="tutorials-form-input"
                                />

                                {tutorialForm.thumbnail && (
                                    <div className="mt-2">
                                        <a
                                            href={tutorialForm.thumbnail}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline break-all"
                                        >
                                            View Uploaded Thumbnail
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="tutorials-form-group">
                                <label className="tutorials-form-label">Order</label>
                                <input
                                    type="number"
                                    value={tutorialForm.order}
                                    onChange={(e) => setTutorialForm({ ...tutorialForm, order: parseInt(e.target.value) })}
                                    className="tutorials-form-input"
                                />
                            </div>
                            <div className="tutorials-modal-actions">
                                <button
                                    type="button"
                                    onClick={() => setEditingTutorial(null)}
                                    className="tutorials-cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="tutorials-submit-button"
                                >
                                    Update Tutorial
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TutorialSystem;